import { Kinds } from "constants/product-collection";
import { AllocationDimensionKind, IAllocationDimension, IAllocationDimensionValue } from "helpers/allocation-dimension";
import {
    GROUP_ID_DELIMITER,
    IDoorGroupsQueryParams,
    DoorGroupParams,
    Order,
    PURCHASE_ORDER_DOOR_GROUP_FIELD,
} from "helpers/door-collection";
import { Metric } from "helpers/metrics/types";
import { ISchemaKey } from "helpers/product-collection";
import { Attribute, Dimension, DimensionValue, DimensionsPanelReduxState } from "../dimensions-panel";
import { mergeQueryParams } from "./url-helpers";
import { customSort } from "helpers/array-utils";
import { setIn } from "helpers/objUtils";
import { buildAttributesId } from "components/product-collection/product-collection-toolbar/product-collection-toolbar-filters/utils";

interface IBootstrapDimensionsPanelStateProps {
    allocationDimensions: IAllocationDimension[];
    initialMetrics: Metric[];
    queryParams?: IDoorGroupsQueryParams;
}

export const mergeAttributesToState = (
    dimensionState: DimensionsPanelReduxState,
    attributesKeys: ISchemaKey[]
): DimensionsPanelReduxState => {
    const attributes = [];

    for (const dimensionAttribute of attributesKeys) {
        if (dimensionAttribute.kind !== Kinds.ALLOCATION_DIMENSION_CLASSIFICATION) {
            continue;
        }

        const existingAttributeIndex = dimensionState.attributes.findIndex(
            (attribute) => attribute.id === dimensionAttribute.field_name
        );
        const existingAttribute = dimensionState.attributes[existingAttributeIndex];
        let attributeValues;

        // PO Door group has to be handled differently since its values is not reliant on allowed_values, but from Merchant PO Door group.
        // These options will not change throughout in an assortment. So it's safe to use existing values from `existingAttribute`
        if (existingAttribute?.id === PURCHASE_ORDER_DOOR_GROUP_FIELD) {
            attributeValues = existingAttribute.values;
        } else {
            attributeValues = buildAllowedValues(dimensionAttribute.constraint?.allowed_values, existingAttribute);
        }

        attributes.push({
            id: dimensionAttribute.field_name,
            sort: existingAttribute?.sort ?? Order.Ascending,
            name: dimensionAttribute.display_name,
            values: attributeValues,
            collectionId: dimensionAttribute.collectionId ?? "",
            isUserDefined: dimensionAttribute.display?.is_user_defined ?? false,
            selected: existingAttribute?.selected ?? false,
            showLabelInHeader: existingAttribute?.showLabelInHeader ?? false,
        });
    }

    // Use existing sort
    customSort(
        attributes,
        dimensionState.attributes,
        (attribute) => attribute.id,
        (attribute) => attribute.id
    );

    function buildAllowedValues(allowedValues: { string_value: string }[], existingAttribute?: Attribute) {
        const wasBlankSelected = existingAttribute?.values.find((value) => value.id === "")?.selected ?? false;
        const blankAllowedValue = {
            id: "",
            label: "",
            selected: wasBlankSelected,
        };

        if (!allowedValues) {
            return [blankAllowedValue];
        }

        const mappedAllowedValues = allowedValues.reduce((acc, allowedValue) => {
            // removing the blank value that was added on creation in older assorments
            if (allowedValue.string_value === "") {
                return acc;
            }

            const wasSelected =
                existingAttribute?.values.find((value) => value.id === allowedValue.string_value)?.selected ?? false;
            return [
                ...acc,
                {
                    id: allowedValue.string_value,
                    label: allowedValue.string_value,
                    selected: wasSelected,
                },
            ];
        }, []);

        const newAllowedValues = [...mappedAllowedValues, blankAllowedValue];

        // When existing sort is custom, we sort it as such
        if (existingAttribute?.sort === Order.Custom) {
            customSort<DimensionValue, DimensionValue>(
                newAllowedValues,
                existingAttribute.values,
                (value) => value.id,
                (value) => value.id
            );
        }

        return newAllowedValues;
    }

    return setIn(dimensionState, "attributes", attributes);
};

export const buildAttributes = (attributesKeys: ISchemaKey[]): Attribute[] => {
    const attributes = [];

    attributesKeys.forEach((dimensionAttribute) => {
        if (dimensionAttribute.kind === Kinds.ALLOCATION_DIMENSION_CLASSIFICATION) {
            attributes.push({
                id: buildAttributesId(dimensionAttribute),
                sort: Order.Ascending,
                name: dimensionAttribute.display_name,
                values: buildAllowedValues(dimensionAttribute.constraint?.allowed_values),
                collectionId: dimensionAttribute.collectionId ?? "",
                isUserDefined: dimensionAttribute.display?.is_user_defined ?? false,
                selected: false,
                showLabelInHeader: false,
            });
        }
    });

    function buildAllowedValues(allowedValues: any) {
        const blankAllowedValue = {
            id: "",
            label: "",
            selected: false,
        };

        if (!allowedValues) {
            return [blankAllowedValue];
        }

        const mappedAllowedValues = allowedValues.reduce((acc, allowedValue) => {
            // removing the blank value that was added on creationg
            // in older assorments
            if (allowedValue.string_value === "") {
                return acc;
            }
            return [
                ...acc,
                {
                    id: allowedValue.string_value,
                    label: allowedValue.string_value,
                    selected: false,
                },
            ];
        }, []);

        return [...mappedAllowedValues, blankAllowedValue];
    }
    return attributes;
};

export const mergeAllocationDimensionValuesToState = (
    dimensionState: DimensionsPanelReduxState,
    dimensionId: string,
    values: IAllocationDimensionValue[]
): DimensionsPanelReduxState => {
    const existingDimensionIndex = dimensionState.dimensions.findIndex((dimension) => dimension.id === dimensionId);

    if (existingDimensionIndex === -1) {
        return dimensionState;
    }

    const existingDimension = dimensionState.dimensions[existingDimensionIndex];
    const dimensionValues = values.reduce((acc, dimensionValue) => {
        if (dimensionValue.active && dimensionValue.included) {
            const wasSelected = existingDimension.values.find(
                (existingDimensionValue) => existingDimensionValue.id === dimensionValue.id
            )?.selected;

            acc.push({
                id: dimensionValue.id,
                label: dimensionValue.name,
                selected: wasSelected ?? dimensionValue.included,
            });
        }
        return acc;
    }, []);

    // When existing sort is custom, we sort it as such
    if (existingDimension.sort === Order.Custom) {
        customSort<DimensionValue, DimensionValue>(
            dimensionValues,
            existingDimension.values,
            (value) => value.id,
            (value) => value.id
        );
    }

    return setIn(dimensionState, `dimensions.${existingDimensionIndex}.values`, dimensionValues);
};

export const buildAllocationDimensionValues = (values: IAllocationDimensionValue[]) =>
    values.reduce((acc, dimensionValue) => {
        if (dimensionValue.active && dimensionValue.included) {
            acc.push({
                id: dimensionValue.id,
                label: dimensionValue.name,
                selected: true,
            });
        }
        return acc;
    }, []);

export const buildDimensionByAllocationDimension = (allocationDimension: IAllocationDimension): Dimension => {
    return {
        id: allocationDimension.id,
        values: buildAllocationDimensionValues(allocationDimension.dimension_values),
        name: allocationDimension.name,
        sort: Order.Ascending,
        selected: true,
    };
};

export const bootstrapDimensionsPanelState = ({
    allocationDimensions,
    initialMetrics,
    queryParams,
}: IBootstrapDimensionsPanelStateProps) => {
    const isAllocationDimensionsListEmpty = Boolean(allocationDimensions.length);
    const dimensionsPanelState = {
        showDoors: Boolean(queryParams?.[DoorGroupParams.Show]),
        // There was a bug where attributes weren't initialized cocrrectly. Defaulting to [] allows us to handle it.
        attributes: isAllocationDimensionsListEmpty
            ? buildAttributes(allocationDimensions[allocationDimensions.length - 1].attributes?.keys ?? [])
            : [],
        metrics: {
            values: initialMetrics,
            showAssortmentTotals: queryParams?.[DoorGroupParams.ShowTotals] || false,
            showAssortmentPlan: queryParams?.[DoorGroupParams.ShowPlanMetrics] || false,
        },
        dimensions: allocationDimensions.reduce((acc, allocationDimension) => {
            if (allocationDimension.active && allocationDimension.subtype === AllocationDimensionKind.Generic) {
                acc.push(buildDimensionByAllocationDimension(allocationDimension));
            }
            return acc;
        }, []),
        collapsedGroupIds: [],
        delimiter: GROUP_ID_DELIMITER,
    };

    queryParams && mergeQueryParams(dimensionsPanelState, queryParams);

    return dimensionsPanelState;
};
